import React, { memo } from 'react';
import { N50Breadcrumb } from '~/features/navigation';
import { P170StoreOverviewPage, P180StorePage as P180StorePageType } from '~/lib/data-contract';
import { usePage } from '~/templates/pages';
import {
    StyledP180,
    StyledP180Content,
    StyledP180ImageWrapper,
    StyledP180BreadcrumbWrapper,
    StyledP180Aside,
    StyledP180ContactText,
    StyledP180FacebookIconWrapper,
} from './styled';
import { ButtonLegacy, LegacyImage, Link, Text } from '~/shared/components';
import { useFrame, useTranslation } from '~/shared/utils';
import { queriesIsolated, Spacer } from '~/theme';
import Call from '$icons/solidAsh/Call.svg';
import Mail from '$icons/solidAsh/mail.svg';
import Facebook from '$icons/facebook.svg';
import { OpeningHoursTable } from '.';
import { usePageRequest } from '~/shared/utils/request';
import { DynamicBlockList } from '~/templates/blocks';
import Head from 'next/head';
import { Container } from '~/templates/blocks/components/container/container.component';

export const P180StorePage = memo(() => {
    const { translate } = useTranslation();
    const { data: frame } = useFrame();
    const { data } = usePageRequest<P170StoreOverviewPage>(frame?.staticLinks?.storesPage?.url);
    const page = usePage<P180StorePageType>();

    const {
        storeImage,
        storeName,
        city,
        zipcode,
        address,
        phone,
        email,
        openingHoursJson,
        facebookLink,
        meta,
    } = page;

    return (
        <>
            {meta?.metaTitle ? null : (
                <Head>
                    <title>{storeName}</title>
                </Head>
            )}
            <Container hasGutter spacingTop="none" spacingBottom="default">
                <StyledP180>
                    <StyledP180BreadcrumbWrapper>
                        <N50Breadcrumb />
                    </StyledP180BreadcrumbWrapper>
                    <StyledP180Content>
                        <Text variant="display2" as="h1">
                            {storeName}
                        </Text>
                        <Spacer space="2" />
                        <Text>
                            {address}, {zipcode} {city}
                        </Text>
                        <Spacer space="5" />
                        {phone && (
                            <Link
                                href={`tel:${phone}`}
                                type="plain"
                                title={translate('stores.overview.tel')}
                            >
                                <StyledP180ContactText as="b">
                                    <Call />
                                    {phone}
                                </StyledP180ContactText>
                            </Link>
                        )}
                        {email && (
                            <>
                                <Spacer space="2" />
                                <Link
                                    href={`mailto:${email}`}
                                    type="plain"
                                    title={translate('stores.overview.mailto')}
                                >
                                    <StyledP180ContactText as="b">
                                        <Mail />
                                        {email}
                                    </StyledP180ContactText>
                                </Link>
                            </>
                        )}
                        <Spacer space="5" />
                        <OpeningHoursTable openingHours={openingHoursJson} />
                        <Spacer space="6" />
                        {facebookLink && (
                            <ButtonLegacy
                                href={facebookLink}
                                as="a"
                                target="_blank"
                                shape="icon"
                                variant="secondary"
                            >
                                <StyledP180FacebookIconWrapper>
                                    <Facebook />
                                </StyledP180FacebookIconWrapper>
                                {translate('stores.page.facebookButtonText')}
                            </ButtonLegacy>
                        )}
                    </StyledP180Content>
                    {storeImage?.src && (
                        <StyledP180Aside>
                            <StyledP180ImageWrapper>
                                <LegacyImage
                                    {...storeImage}
                                    layout="fill"
                                    objectFit="cover"
                                    sizes={`${queriesIsolated.xs} 100vw, ${queriesIsolated.sm} 50vw, 60vw`}
                                    cW={4}
                                    cH={3}
                                    src={storeImage.src}
                                />
                            </StyledP180ImageWrapper>
                        </StyledP180Aside>
                    )}
                </StyledP180>
            </Container>
            <DynamicBlockList elements={data?.relatedPageElements || []} />
        </>
    );
});
